import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  TrendingUp,
  AccountBalance,
  Savings,
  Assessment,
  CloudDownload,
  Sync,
} from '@mui/icons-material';
import AdminLayout from '../components/admin/AdminLayout';
import CreateLinkDialog from '../components/admin/CreateLinkDialog';
import PaymentLinksTable from '../components/admin/PaymentLinksTable';
import LinkDetailsDialog from '../components/admin/LinkDetailsDialog';
import ReportsDialog from '../components/admin/ReportsDialog';
import BackupDialog from '../components/admin/BackupDialog';
import AutoBackupDialog from '../components/admin/AutoBackupDialog';
import { usePaymentLinks, usePaymentStats } from '../hooks/usePaymentLinks';
import { PaymentLink } from '../types/PaymentLink';
import { formatCurrency } from '../utils/validators';
import { getCurrentUser, isSuperAdmin } from '../services/authService';

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { paymentLinks, loading } = usePaymentLinks();
  const stats = usePaymentStats(paymentLinks);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [autoBackupDialogOpen, setAutoBackupDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<PaymentLink | null>(null);
  const [_refreshKey, setRefreshKey] = useState(0);

  // Check if current user is superuser
  const currentUser = getCurrentUser();
  const isSuperUser = isSuperAdmin(currentUser);

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleViewDetails = (paymentLink: PaymentLink) => {
    setSelectedLink(paymentLink);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedLink(null);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // Statistics cards configuration
  // Updated: Removed "Total Links", added "Total Collected", renamed "Total Amount" to "Total Outstanding"
  const statsCards = [
    {
      title: 'Active Links',
      value: stats.activeLinks,
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#ff9800',
      bgColor: '#fff3e0',
    },
    {
      title: 'Successful Payments',
      value: stats.successfulPayments,
      icon: <CheckCircle sx={{ fontSize: 40 }} />,
      color: '#4caf50',
      bgColor: '#e8f5e9',
    },
    {
      title: 'Total Collected',
      value: formatCurrency(stats.totalCollected, 'EUR'),
      icon: <Savings sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      bgColor: '#e8f5e9',
    },
    {
      title: 'Total Outstanding',
      value: formatCurrency(stats.totalOutstanding, 'EUR'),
      icon: <AccountBalance sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      bgColor: '#e3f2fd',
    },
  ];

  return (
    <AdminLayout>
      <Box>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              Payment Link Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and monitor all payment links
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {isSuperUser && (
              <>
                <Button
                  variant="outlined"
                  size={isMobile ? 'medium' : 'large'}
                  startIcon={<Sync />}
                  onClick={() => setAutoBackupDialogOpen(true)}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                  }}
                >
                  Automated Backups
                </Button>
                <Button
                  variant="outlined"
                  size={isMobile ? 'medium' : 'large'}
                  startIcon={<CloudDownload />}
                  onClick={() => setBackupDialogOpen(true)}
                  sx={{
                    borderColor: '#9c27b0',
                    color: '#9c27b0',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                  }}
                >
                  Backup
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              size={isMobile ? 'medium' : 'large'}
              startIcon={<Assessment />}
              onClick={() => setReportsDialogOpen(true)}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.5 },
              }}
            >
              Reports
            </Button>
            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              startIcon={<Add />}
              onClick={handleOpenCreateDialog}
              sx={{
                background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.5 },
              }}
            >
              Create New Link
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statsCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${stat.bgColor} 0%, #ffffff 100%)`,
                  borderLeft: `4px solid ${stat.color}`,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {stat.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: stat.color,
                          mt: 1,
                          fontSize: { xs: '1.75rem', sm: '2.125rem' },
                        }}
                      >
                        {stat.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        color: stat.color,
                        opacity: 0.7,
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Payments Table */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            Payments
          </Typography>
          {loading ? (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Loading...</Typography>
            </Card>
          ) : (
            <PaymentLinksTable
              paymentLinks={paymentLinks}
              onViewDetails={handleViewDetails}
              onRefresh={handleRefresh}
            />
          )}
        </Box>
      </Box>

      {/* Dialogs */}
      <CreateLinkDialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        onSuccess={handleCreateSuccess}
      />

      <LinkDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        paymentLink={selectedLink}
        onDeleted={handleRefresh}
      />

      <ReportsDialog
        open={reportsDialogOpen}
        onClose={() => setReportsDialogOpen(false)}
        paymentLinks={paymentLinks}
      />

      <BackupDialog
        open={backupDialogOpen}
        onClose={() => setBackupDialogOpen(false)}
      />

      <AutoBackupDialog
        open={autoBackupDialogOpen}
        onClose={() => setAutoBackupDialogOpen(false)}
      />
    </AdminLayout>
  );
};

export default AdminDashboard;
